import React, { useState, useEffect, useContext } from 'react';
import { AutoComplete, Button, Col, Form, Input, Row, Select, Switch } from 'antd';
import { UserType } from '../../types/types';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createUser, updateUser, setCurrentUser, getAllUsers, initialState } from '../../features/user/userSlice';

import { NotificationContext } from '../../providers/NotificationProvider';
import paises from '../../constants/paises.json';
import { constants } from '../../constants/constants';
import CompanyUsers from './CompanyUsers';
import { useTranslation } from 'react-i18next';

interface CreateOrEditUserProps {
  currentUser?: UserType | null;
  viewOnly?: boolean;
}

const CreateOrEditUser: React.FC<CreateOrEditUserProps> = ({ viewOnly = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);
  const [form] = Form.useForm();
  const { user, currentUser, users } = useSelector((store: any) => store.user);
  const rol = useSelector((store: any) => store.user.rol);
  const { id } = useParams();

  const [isEdit, setIsEdit] = useState<boolean>(!viewOnly);
  const [isViewOnly, setIsViewOnly] = useState<boolean>(viewOnly);
  const [options, setOptions] = useState<{ value: string; phoneCode: string }[]>([]);
  const [isEmpresa, setIsEmpresa] = useState<boolean>(false);
  //const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isEmployee, setIsEmployee] = useState<boolean>(false);
  //const [isIndependiente, setIsIndependiente] = useState<boolean>(false);

  const companyList = users.filter((user: UserType) => user?.groups?.includes(2));

  useEffect(() => {
    dispatch(getAllUsers() as any)
      .then((result: any) => {
        if (getAllUsers.fulfilled.match(result)) {
          if (!id || parseInt(id) === 0) return;
          const cUser = result.payload.filter((user: UserType) => user.id === parseInt(id))?.[0];
          dispatch(setCurrentUser(cUser));
        } else {
          triggerNotification('error', t('Error'), t('Error al cargar el usuario'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al cargar el usuario'), 'bottomRight');
      });
    // eslint-disable-next-line
  }, []);

  const onSearch = (searchText: string) => {
    const filteredOptions = paises
      .filter(
        (pais) => pais.nameES.toLowerCase().includes(searchText.toLowerCase()) || pais.phoneCode.includes(searchText)
      )
      .map((pais) => ({
        value: `${pais.nameES} (+${pais.phoneCode})`,
        phoneCode: pais.phoneCode,
      }));
    setOptions(filteredOptions);
  };

  const onSelect = (value: string) => {
    const selectedCode = options.find((option) => option.value === value)?.phoneCode || '';
    form.setFieldsValue({ callcode: selectedCode });
  };

  const onFinishFailed = (errorInfo: any) => {
    triggerNotification(
      'error',
      t('Error'),
      t('Hay errores en el formulario. Por favor, corrige los campos marcados.'),
      'bottomRight'
    );
  };

  useEffect(() => {
    if (currentUser && id) {
      form.setFieldsValue({
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        company_name: currentUser.company_name,
        email: currentUser.email,
        username: currentUser.username,
        is_active: currentUser.is_active,
        company: currentUser.company,
        callcode: currentUser.callcode,
        phone: currentUser.phone,
        position: currentUser.position,
        groups: currentUser.groups?.map((group: any) => group),
      });

      const isGroupEmpresa = currentUser?.groups?.includes(2);
      setIsEmpresa(isGroupEmpresa);
    }
  }, [currentUser, form, id]);

  // Manejar cambios en los valores del formulario
  const onValuesChange = (changedValues: any, allValues: any) => {
    if (!id) {
      const isGroupEmpresa = allValues?.groups === 2;
      setIsEmpresa(isGroupEmpresa);
      //const isGroupAdmin = allValues?.groups === 1;
      //setIsAdmin(isGroupAdmin);
      const isGroupEmployee = allValues?.groups === 3;
      setIsEmployee(isGroupEmployee);
      //const isGroupIndependiente = allValues?.groups === 4;
      //setIsIndependiente(isGroupIndependiente);
    }
  };

  const onFinish = async (values: any) => {
    if (isEdit) {
      if (!id) {
        const userData: UserType = {
          ...values,
          id: null,
          first_name: values.first_name,
          last_name: values.last_name,
          company_name: values.company_name,
          email: values.email,
          username: values.username,
          is_active: values.is_active,
          company: values.company || null,
          callcode: values.callcode,
          phone: values.phone,
          position: values.position,
          groups: values.groups || [],
        };
        if (rol === constants.ROL_EMPRESA) {
          userData.groups = 3;
          userData.company = user.id;
        }
        if (userData.groups.length === 0) {
          triggerNotification('error', t('Error'), t('Debe seleccionar al menos un rol.'), 'bottomRight');
          return;
        }

        if (!userData.callcode || userData.callcode?.length === 0) {
          triggerNotification('error', t('Error'), t('Debe seleccionar un código de país.'), 'bottomRight');
          return;
        }

        if (userData.groups === 2) {
          userData.first_name = 'N/A';
          userData.last_name = 'N/A';
        } else {
          if (userData.groups === 3) {
            if (!userData.company) {
              triggerNotification('error', t('Error'), t('Debe seleccionar una empresa.'), 'bottomRight');
              return;
            }
            if (!userData.position || userData.position?.length === 0) {
              triggerNotification('error', t('Error'), t('Debe ingresar una posición.'), 'bottomRight');
              return;
            }
          }
          userData.company_name = 'N/A';
        }
        await createUserAction(userData);
      } else {
        const userData: UserType = {
          ...currentUser,
          id: currentUser?.id,
          first_name: values.first_name ?? currentUser?.first_name,
          last_name: values.last_name ?? currentUser?.last_name,
          company_name: values.company_name ?? currentUser?.company_name,
          email: values.email ?? currentUser?.email,
          username: values.username ?? currentUser?.username,
          is_active: values.is_active ?? currentUser?.is_active,
          company: values.company ?? currentUser.company,
          callcode: values.callcode ?? currentUser?.callcode,
          phone: values.phone ?? currentUser?.phone,
          position: values.position ?? currentUser?.position,
          groups: values.groups ?? currentUser?.groups,
        };

        if (userData.callcode?.length === 0) {
          triggerNotification('error', t('Error'), t('Debe seleccionar un código de país.'), 'bottomRight');
          return;
        }

        if (rol === constants.ROL_EMPRESA) {
          userData.groups = 3;
          userData.company = user.id;
        }
        await updateUserAction(userData);
      }
    }
  };

  const createUserAction = async (userData: UserType) => {
    try {
      const result = await dispatch(createUser(userData) as any);
      if (createUser.fulfilled.match(result)) {
        triggerNotification('success', t('Creación Exitosa'), t('El usuario se ha creado exitosamente.'), 'topRight');
        setCurrentUser(initialState.currentUser);
        form.resetFields();
        navigate('/usuarios');
      } else {
        triggerNotification('error', t('Error'), result.payload.message, 'topRight');
      }
    } catch (error: any) {
      triggerNotification('error', t('Error'), error.message, 'topRight');
    }
  };

  const updateUserAction = async (userData: UserType) => {
    try {
      const result = await dispatch(updateUser(userData) as any);
      if (updateUser.fulfilled.match(result)) {
        triggerNotification(
          'success',
          t('Actualización Exitosa'),
          t('El usuario se ha actualizado exitosamente.'),
          'topRight'
        );
        setCurrentUser(initialState.currentUser);
        form.resetFields();
        navigate('/usuarios');
      } else {
        triggerNotification('error', t('Error'), result.payload.message, 'topRight');
      }
    } catch (error: any) {
      triggerNotification('error', t('Error'), error.message, 'topRight');
    }
  };

  const goBack = () => {
    setCurrentUser(initialState.currentUser);
    form.resetFields();
    navigate('/usuarios');
  };

  return (
    <Row className='content'>
      <Col sm={6}>
        <Form
          form={form}
          layout='vertical'
          onFinish={onFinish}
          className={isViewOnly ? 'viewOnly' : ''}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
        >
          {(isViewOnly || isEdit) && id && currentUser?.is_staff ? (
            <span style={{ marginBottom: 15, display: 'block' }}>
              <b>{t('Rol:')} </b> {t('Superusuario')}
            </span>
          ) : (
            (rol === constants.ROL_ADMIN || user?.is_staff) &&
            !id && (
              <Form.Item label={t('Rol')} name='groups'>
                <Select disabled={isViewOnly}>
                  <Select.Option value={1}>{t(constants.ROL_ADMIN)}</Select.Option>
                  <Select.Option value={2}>{t(constants.ROL_EMPRESA)}</Select.Option>
                  <Select.Option value={3}>{t(constants.ROL_USER)}</Select.Option>
                  <Select.Option value={4}>{t(constants.ROL_INDEPENDIENTE)}</Select.Option>
                  {/* Los roles deben ser traídos desde la API */}
                </Select>
              </Form.Item>
            )
          )}
          {isEmpresa ? (
            <Form.Item label={t('Nombre de Empresa')} name='company_name'>
              <Input disabled={isViewOnly} required />
            </Form.Item>
          ) : (
            <>
              <Form.Item label={t('Nombres')} name='first_name'>
                <Input disabled={isViewOnly} required />
              </Form.Item>
              <Form.Item label={t('Apellidos')} name='last_name'>
                <Input disabled={isViewOnly} required />
              </Form.Item>
            </>
          )}
          <Form.Item label={t('Correo electrónico')} name='email'>
            <Input disabled={isViewOnly} type='email' required />
          </Form.Item>
          <Form.Item label={t('Nombre de Usuario')} name='username'>
            <Input disabled={isViewOnly} required />
          </Form.Item>
          <Form.Item label={t('Activo')} name='is_active' valuePropName='checked'>
            <Switch disabled={isViewOnly} />
          </Form.Item>
          <Row gutter={4}>
            <Col sm={6}>
              <Form.Item label={t('Indicativo')} name='callcode'>
                <AutoComplete
                  disabled={isViewOnly}
                  options={options.map((option) => ({ value: option.value }))}
                  onSearch={onSearch}
                  onSelect={onSelect}
                  placeholder={t('Buscar país o código')}
                  filterOption={(inputValue, option) =>
                    option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
              </Form.Item>
            </Col>
            <Col sm={18}>
              <Form.Item label={t('Teléfono')} name='phone'>
                <Input disabled={isViewOnly} maxLength={10} minLength={10} required />
              </Form.Item>
            </Col>
          </Row>

          {(currentUser?.groups?.includes(3) || isEmployee) && (
            <>
              {rol === constants.ROL_EMPRESA ? (
                <Form.Item label={t('Posición')} name='position'>
                  <Input disabled={isViewOnly} maxLength={80} />
                </Form.Item>
              ) : (
                <>
                  <Form.Item label={t('Empresa')} name='company'>
                    <Select>
                      {companyList.map((company: UserType) => (
                        <Select.Option key={company.id} value={company.id}>
                          {company.company_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label={t('Posición')} name='position'>
                    <Input disabled={isViewOnly} maxLength={80} />
                  </Form.Item>
                </>
              )}
            </>
          )}

          {!isViewOnly && (
            <Form.Item>
              <Button type='primary' htmlType='submit'>
                {isEdit ? t('Guardar Cambios') : t('Crear Usuario')}
              </Button>
              <span className='ant-divider' />
              <Button type='default' htmlType='button' onClick={goBack}>
                {t('Cancelar')}
              </Button>
            </Form.Item>
          )}
        </Form>
        {isViewOnly && (
          <>
            <Button
              type='primary'
              onClick={() => {
                setIsViewOnly(false);
                setIsEdit(true);
              }}
              htmlType='button'
            >
              {t('Editar')}
            </Button>
            <span className='ant-divider' />
            <Button type='default' onClick={goBack} htmlType='button'>
              {t('Volver')}
            </Button>
          </>
        )}
      </Col>
      <Col sm={18}>{id && currentUser?.groups.includes(2) ? <CompanyUsers company_id={parseInt(id)} /> : null}</Col>
    </Row>
  );
};

export default CreateOrEditUser;
